// Pure helpers for handling files dropped or pasted into the page editor.
// Kept free of React/TipTap imports so they can be unit-tested in isolation.

// The subset of DataTransfer the editor's paste/drop handlers read. Typed
// structurally so tests can pass plain objects without a real DataTransfer.
interface FileTransfer {
    files?: ArrayLike<File> | null;
    items?: ArrayLike<{ kind: string; type: string; getAsFile: () => File | null }> | null;
}

// Pull every file out of a clipboard or drag-and-drop payload, regardless of
// type. Screenshots pasted from the OS clipboard land in `items` rather than
// `files` in some browsers, so fall back to `items` when `files` is empty.
// Partitioning images vs. other files is left to the caller.
export function extractFiles(data: FileTransfer | null): File[] {
    if (!data) return [];
    const fromFiles = Array.from(data.files || []);
    if (fromFiles.length > 0) return fromFiles;
    const fromItems: File[] = [];
    for (const item of Array.from(data.items || [])) {
        if (item.kind === "file") {
            const file = item.getAsFile();
            if (file) fromItems.push(file);
        }
    }
    return fromItems;
}

export function escapeHtml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

// Build the inline link inserted for a non-image attachment: a paperclip plus
// the filename, pointing at the attachment download URL. Both the URL and the
// filename are escaped because they flow into editor HTML.
export function buildAttachmentLinkHtml(url: string, filename: string): string {
    return `<a href="${escapeHtml(url)}">📎 ${escapeHtml(filename)}</a> `;
}
